import { useMemo } from "react";
import useSWRInfinite, { type SWRInfiniteResponse } from "swr/infinite";

import { fetchClaims } from "lib/indexer/client";

import type { ClaimAction } from "./types";

export type ClaimCollateralHistoryResult = {
  claimActions?: ClaimAction[];
  isLoading: boolean;
  pageIndex: number;
  setPageIndex: (...args: Parameters<SWRInfiniteResponse["setSize"]>) => void;
};

export function useClaimCollateralHistory(
  chainId: number,
  p: {
    pageSize: number;
    fromTxTimestamp?: number;
    toTxTimestamp?: number;
    eventName?: string[];
    marketAddresses?: string[];
  }
): ClaimCollateralHistoryResult {
  const { pageSize } = p;

  const key = (pageIndex: number) => [chainId, "useClaimHistory", pageIndex, pageSize];

  const {
    data,
    error,
    size: pageIndex,
    setSize: setPageIndex,
  } = useSWRInfinite<ClaimAction[]>(key, {
    fetcher: async (key) => {
      const pageIdx = key[2] as number;
      // V1 platform has no Synthetics claim events (funding fees / price impact rebates).
      // The indexer returns an empty list and will be populated when V2 markets are live.
      const res = await fetchClaims({ page: pageIdx + 1, pageSize });
      return res.items as ClaimAction[];
    },
  });

  const isLoading = !error && !data;
  const claimActions = useMemo(() => data?.flat() ?? [], [data]);

  return { claimActions, isLoading, pageIndex, setPageIndex };
}
